/**
 * Full-page FlexFlow form for BYO destination configuration.
 * Same pattern as ByoSourceSetupForm but for SINK:
 * Connect → Authenticate → Configure (FileSystem for output folder).
 */

import React, { useRef } from 'react';
import { Form } from 'new/elements/Form';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { ConnectStep } from 'new/components/flows/types/DirectFlow/steps/ConnectStep';
import { AuthenticateStep } from 'new/components/flows/types/DirectFlow/steps/AuthenticateStep';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import { useStartSink } from 'new/components/flows/types/DirectFlow/hooks/useStartSink';
import { HeaderMetaConfigProvider } from 'new/components/flows/context/HeaderMetaConfigProvider';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import { StudioDraftProvider } from '../studio/StudioDraftContext';
import { ByoDestinationConfigureStep } from './ByoDestinationConfigureStep';

interface ByoDestinationSetupFormProps {
  onDismiss: (data?: any) => void;
  sourceId?: number | string | null;
  nexsetId?: number | null;
  id?: number;
  isEditMode?: boolean;
}

function ByoDestinationFormInner({ onDismiss, isEditMode }: ByoDestinationSetupFormProps) {
  const start = useRef(true);
  const { onStartForm, updateFormData } = useDirectFlow();

  if (start.current) {
    onStartForm();
    updateFormData({ flowType: 'custom', connectionType: 'SINK' });
    start.current = false;
  }

  useStartSink();

  return (
    <HeaderMetaConfigProvider>
      <Form
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        isModal={false}
        isEditMode={isEditMode ?? false}
        // In edit mode the sink is already configured — open the Configure step
        // (index 2: Connect=0, Authenticate=1, Configure=2) with values prefilled
        // by useStartSink, matching CreateDirectFlow's edit behavior.
        startPage={isEditMode ? 2 : undefined}
        onDismiss={() => {
          onDismiss();
          return true;
        }}
      >
        <ConnectStep />
        <AuthenticateStep />
        <ByoDestinationConfigureStep />
      </Form>
    </HeaderMetaConfigProvider>
  );
}

export function ByoDestinationSetupForm({
  onDismiss,
  sourceId,
  nexsetId,
  id,
  isEditMode,
}: ByoDestinationSetupFormProps) {
  return (
    <StudioDraftProvider>
      <FlowParamsProvider
        flowType="custom"
        resourceType="sink"
        isModal
        id={id}
        isEditMode={isEditMode}
        onDismiss={onDismiss}
        sourceId={sourceId != null ? Number(sourceId) : undefined}
        nexsetId={nexsetId ?? undefined}
      >
        <ByoDestinationFormInner
          onDismiss={onDismiss}
          sourceId={sourceId}
          nexsetId={nexsetId}
          id={id}
          isEditMode={isEditMode}
        />
      </FlowParamsProvider>
    </StudioDraftProvider>
  );
}

export default ByoDestinationSetupForm;
